import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Trash2, UserPlus, X } from 'lucide-react';
import { confirm } from '@/components/ui/confirm';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface BulkActionBarProps {
  selectedCount: number;
  onComplete: () => void;
  onDelete: () => void;
  onReassign: (userId: string) => void;
  onClear: () => void;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onComplete,
  onDelete,
  onReassign,
  onClear,
}) => {
  const { data: companyMembers } = useCompanyMembers();

  if (selectedCount === 0) return null;

  const handleDelete = () => {
    confirm({
      title: 'Delete Tasks',
      description: `Are you sure you want to delete ${selectedCount} task(s)? This action cannot be undone.`,
      onConfirm: onDelete,
    });
  };

  const handleComplete = () => {
    confirm({
      title: 'Complete Tasks',
      description: `Mark ${selectedCount} task(s) as completed?`,
      onConfirm: onComplete,
    });
  };

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-card border border-border shadow-lg rounded-lg px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4">
      <span className="text-sm font-medium text-foreground">
        {selectedCount} selected
      </span>
      
      <div className="h-4 w-px bg-border" />
      
      <Button variant="outline" size="sm" onClick={handleComplete} className="gap-1.5">
        <Check className="h-3.5 w-3.5" />
        Complete
      </Button>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <UserPlus className="h-3.5 w-3.5" />
            Reassign
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="max-h-60 overflow-y-auto">
          {companyMembers?.map((member) => (
            <DropdownMenuItem
              key={member.id}
              onClick={() => onReassign(member.id)}
              className="flex items-center gap-2"
            >
              <Avatar className="h-5 w-5">
                <AvatarImage src={member.avatar_url || ''} />
                <AvatarFallback className="text-[9px]">
                  {member.first_name?.[0]}{member.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <span>{member.first_name} {member.last_name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Button variant="destructive" size="sm" onClick={handleDelete} className="gap-1.5">
        <Trash2 className="h-3.5 w-3.5" />
        Delete
      </Button>
      
      <div className="h-4 w-px bg-border" />
      
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClear}>
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

export default BulkActionBar;
